import {
  ConnectParamsError,
  makeRealtimeSocket,
  type RealtimeTransportFactory,
  type RealtimeSocketErroredEvent,
  type RealtimeTopicJoinStalledEvent,
  type RealtimeSocket as SDKRealtimeSocket,
  topic as realtimeTopic,
  makeQuerySession,
  type QueryClosed,
  type SDKCommand,
  type SDKRequestFor,
  type SDKResponseFor
} from '@st/sdk'
import { RuntimeDefectReporter } from '@st/redux'
import { Context, Effect, Layer, Queue, type Scope, Stream } from 'effect'
import { BrowserLifecycle } from './browser-lifecycle'

type SocketClientMessage = {
  type: string
  data: unknown
}

/** The user topic carries server messages only; state comes from live queries (`query`). */
export type SocketTopic = 'user'

export type SocketTopicEvent<_Topic extends SocketTopic> = {
  type: 'messageReceived'
  message: SocketClientMessage
}

type SocketService = {
  connect: () => Effect.Effect<void>
  disconnect: () => Effect.Effect<void>
  events: <const Topic extends SocketTopic>(topic: Topic) => Stream.Stream<SocketTopicEvent<Topic>>
  query: <K extends SDKCommand['type']>(
    request: SDKRequestFor<K>
  ) => Stream.Stream<SDKResponseFor<K>, QueryClosed>
}

type AnySocketTopicEvent = SocketTopicEvent<SocketTopic>

export class Socket extends Context.Service<Socket, SocketService>()('Socket') {}

/**
 * Keeps the realtime transport desired for exactly one owning Task session.
 * Replacing that Task awaits disconnect before the next session connects,
 * which prevents an authenticated transport from surviving a user change.
 */
export const maintainSocketConnection: Effect.Effect<never, never, Socket> = Effect.gen(
  function* () {
    const socket = yield* Socket
    yield* socket.connect()
    return yield* Effect.never
  }
).pipe(
  Effect.ensuring(
    Effect.gen(function* () {
      const socket = yield* Socket
      yield* socket.disconnect()
    })
  )
)

/**
 * Serializable events for one desired realtime topic.
 *
 * Feature modules use this as a state-owned Task program, so replacing the
 * topic arguments closes the old Stream and suppresses any output already
 * queued by that session.
 */
export function observeSocketTopic<const Topic extends SocketTopic>(
  topic: Topic
): Stream.Stream<SocketTopicEvent<Topic>, never, Socket> {
  return Stream.unwrap(Effect.map(Effect.service(Socket), (socket) => socket.events(topic)))
}

/**
 * The answers of a live query, as a state-owned Task program: the first
 * element is the current answer, later ones are the answer after each change.
 */
export function query<K extends SDKCommand['type']>(
  request: SDKRequestFor<K>
): Stream.Stream<SDKResponseFor<K>, QueryClosed, Socket> {
  return Stream.unwrap(Effect.map(Effect.service(Socket), (socket) => socket.query(request)))
}

type SocketRealtimeClient = Pick<
  SDKRealtimeSocket,
  'connect' | 'disconnect' | 'subscribe' | 'push' | 'connectedTopics' | 'rejoin'
>

export type SocketRuntimeConfigService = {
  endpoint: string
  getCsrfToken: Effect.Effect<string, unknown, never>
  makeTransport?: RealtimeTransportFactory
  onSocketErrored?: (event: RealtimeSocketErroredEvent) => Effect.Effect<void>
  onTopicJoinStalled: (event: RealtimeTopicJoinStalledEvent) => Effect.Effect<void>
}

export class SocketRuntimeConfig extends Context.Service<
  SocketRuntimeConfig,
  SocketRuntimeConfigService
>()('SocketRuntimeConfig') {}

/**
 * Adapter from low-level RealtimeSocket primitives to app-facing socket service semantics.
 * Exported so this critical translation layer can be tested deterministically without a real transport.
 */
export function makeSocketServiceFromRealtime(
  realtime: SocketRealtimeClient
): Effect.Effect<SocketService, never, Scope.Scope> {
  const events = <const Topic extends SocketTopic>(
    topic: Topic
  ): Stream.Stream<SocketTopicEvent<Topic>> =>
    Stream.callback<SocketTopicEvent<Topic>>((queue) =>
      realtime.subscribe(realtimeTopic(topic), (event) =>
        decodeSocketEvent(event.event, event.payload).pipe(
          Effect.flatMap((decoded) => {
            if (!decoded) {
              return Effect.void
            }

            return Effect.sync(() => {
              Queue.offerUnsafe(queue, decoded as SocketTopicEvent<Topic>)
            })
          })
        )
      )
    )

  return Effect.map(makeQuerySession(realtime), (session) => ({
    connect: () => realtime.connect,
    disconnect: () => realtime.disconnect,
    events,
    query: (request) => session.query(request)
  }))
}

export function socketLiveLayer() {
  return Layer.effect(
    Socket,
    Effect.gen(function* () {
      const config = yield* SocketRuntimeConfig
      const lifecycle = yield* BrowserLifecycle
      const defectReporter = yield* RuntimeDefectReporter
      const realtime = yield* makeRealtimeSocket({
        endpoint: config.endpoint,
        getConnectParams: config.getCsrfToken.pipe(
          // Phoenix websocket transport expects `vsn`; keep parity with phoenix.js connect URL.
          Effect.map((token) => ({ _csrf_token: token, vsn: '2.0.0' })),
          Effect.mapError((cause) => new ConnectParamsError('failed to fetch csrf token', cause))
        ),
        lifecycle,
        makeTransport: config.makeTransport,
        onSocketErrored: config.onSocketErrored,
        topicJoinStall: {
          timeout: '20 seconds',
          onStalled: config.onTopicJoinStalled
        },
        onDefect: ({ context, error }) =>
          defectReporter.report(`realtime socket: ${context}`, error)
      })
      return yield* makeSocketServiceFromRealtime(realtime)
    })
  )
}

function decodeSocketEvent(
  eventName: string,
  payload: unknown
): Effect.Effect<AnySocketTopicEvent | null> {
  switch (eventName) {
    case 'client:message': {
      const message = decodeClientMessage(payload)
      if (!message) {
        return malformedSocketPayload(eventName, payload)
      }
      return Effect.succeed({
        type: 'messageReceived',
        message
      })
    }

    default:
      return Effect.succeed(null)
  }
}

function malformedSocketPayload(eventName: string, payload: unknown): Effect.Effect<never> {
  return Effect.die(new Error(`[socket] malformed ${eventName} payloadType=${typeof payload}`))
}

function decodeClientMessage(payload: unknown): SocketClientMessage | null {
  if (typeof payload != 'object' || payload == null) {
    return null
  }

  const value = payload as { type?: unknown; data?: unknown }
  if (typeof value.type != 'string') {
    return null
  }

  return {
    type: value.type,
    data: value.data
  }
}
